import { createFileRoute, useParams, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getRebookCandidates, inviteBartenderToShift, type RebookCandidate } from "@/lib/rebook.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { haptic } from "@/lib/haptics";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/venue/rebook/$shiftId")({
  head: () => ({
    meta: [
      { title: "Find a substitute — The Hospitality Reserve" },
      { name: "description", content: "Auto-rebook substitutes ranked by history, rating and distance." },
    ],
  }),
  component: RebookPage,
});

function RebookPage() {
  const { shiftId } = useParams({ from: "/_authenticated/venue/rebook/$shiftId" });
  const fetchCandidates = useServerFn(getRebookCandidates);
  const invite = useServerFn(inviteBartenderToShift);
  const [rows, setRows] = useState<RebookCandidate[]>([]);
  const [invited, setInvited] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCandidates({ data: { shift_id: shiftId } })
      .then(setRows)
      .finally(() => setLoading(false));
  }, [shiftId, fetchCandidates]);

  async function sendInvite(bid: string, name: string | null) {
    try {
      await invite({ data: { shift_id: shiftId, bartender_id: bid } });
      haptic("success");
      setInvited((s) => new Set([...s, bid]));
      toast.success(`Invite sent to ${name ?? "bartender"}`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Could not send invite.");
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="max-w-3xl mx-auto px-4 py-10 space-y-6">
        <header>
          <h1 className="text-3xl font-semibold">Find a substitute</h1>
          <p className="text-muted-foreground mt-1">
            Ranked by past shifts with you, ratings and distance. Tap to send a direct invite — they can accept in one tap.
          </p>
        </header>
        {loading ? <p className="text-muted-foreground">Finding substitutes…</p> : rows.length === 0 ? (
          <p className="text-muted-foreground">No eligible substitutes yet. Try widening the shift's requirements or check back shortly.</p>
        ) : (
          <ul className="space-y-3">
            {rows.map((c) => (
              <li key={c.bartender_id} className="border rounded-xl p-4 bg-card flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium">
                    {c.full_name ?? "Bartender"}
                    {c.is_favorite && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700">★ Favourite</span>}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {c.completed_with_venue > 0 ? `${c.completed_with_venue} past shift${c.completed_with_venue > 1 ? "s" : ""} with you` : "New to your venue"}
                    {c.avg_rating ? ` · ${c.avg_rating.toFixed(1)}★` : ""}
                    {c.distance_km != null ? ` · ${c.distance_km.toFixed(1)} km away` : ""}
                  </p>
                </div>
                <Button
                  size="sm"
                  disabled={invited.has(c.bartender_id)}
                  onClick={() => sendInvite(c.bartender_id, c.full_name)}
                >
                  {invited.has(c.bartender_id) ? "Invited" : "Invite"}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-sm">
          <Link to="/venue" className="underline">Back to dashboard</Link>
        </p>
      </main>
    </div>
  );
}
